import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";

import { Button } from "@/ui/components/Button";
import { Card, CardContent } from "@/ui/components/Card";
import { Badge } from "@/ui/components/Badge";
import {
  api,
  getGoogleIdToken,
  setGoogleIdToken,
  type SubmissionResponse,
} from "@/lib/api";

// Google Identity Services API surface we touch — typed loosely to
// avoid shipping the full @types/google.accounts dep.
interface GsiCredentialResponse {
  credential: string;
}
interface GsiId {
  initialize(opts: { client_id: string; callback: (resp: GsiCredentialResponse) => void; auto_select?: boolean }): void;
  renderButton(parent: HTMLElement, opts: { theme?: string; size?: string; type?: string; text?: string }): void;
  prompt(): void;
  disableAutoSelect(): void;
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GsiId } };
  }
}

export function Admin() {
  const [tokenVersion, bumpToken] = useState(0);

  const cfg = useQuery({ queryKey: ["app-config"], queryFn: api.config });
  const me = useQuery({
    queryKey: ["me", tokenVersion],
    queryFn: api.me,
  });
  const list = useQuery({
    queryKey: ["admin-submissions", tokenVersion],
    queryFn: api.listSubmissions,
    enabled: Boolean(me.data?.is_admin),
  });

  if (cfg.isLoading || me.isLoading) return null;

  if (!me.data?.is_admin) {
    return (
      <SignInPrompt
        clientId={cfg.data?.google_client_id ?? ""}
        haveToken={Boolean(getGoogleIdToken())}
        deniedEmail={me.data?.email ?? ""}
        onSignedIn={() => bumpToken((n) => n + 1)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-6">
          <div>
            <h1 className="font-display text-2xl font-semibold leading-tight">
              Survey submissions
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {list.data?.length ?? 0} submission{list.data?.length === 1 ? "" : "s"} ·
              {" "}signed in as <span className="font-mono">{me.data.email}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild>
              <a href={api.csvUrl()} download>
                <Download /> Export CSV
              </a>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                window.google?.accounts?.id?.disableAutoSelect();
                setGoogleIdToken("");
                bumpToken((n) => n + 1);
              }}
            >
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {list.isLoading && (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          )}
          {list.isError && (
            <p className="p-6 text-sm text-destructive">
              Couldn't load submissions. {(list.error as Error)?.message}
            </p>
          )}
          {list.data && list.data.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">No submissions yet.</p>
          )}
          {list.data && list.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Submitted</th>
                    <th className="px-4 py-3 font-semibold">Company</th>
                    <th className="px-4 py-3 font-semibold">User</th>
                    <th className="px-4 py-3 font-semibold">MID</th>
                    <th className="px-4 py-3 font-semibold">Business model</th>
                    <th className="px-4 py-3 font-semibold">PAS / Corporate</th>
                    <th className="px-4 py-3 font-semibold">Contract</th>
                    <th className="px-4 py-3 font-semibold text-right">Shop rate</th>
                    <th className="px-4 py-3 font-semibold">Follow-up</th>
                    <th className="px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {list.data.map((s) => <Row key={s.id} s={s} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Sign-in prompt ---------------------------------------------------

function SignInPrompt({
  clientId, haveToken, deniedEmail, onSignedIn,
}: {
  clientId: string;
  haveToken: boolean;
  deniedEmail: string;
  onSignedIn: () => void;
}) {
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    let attempts = 0;

    const init = () => {
      if (cancelled) return;
      const gid = window.google?.accounts?.id;
      if (!gid) {
        if (attempts++ < 50) setTimeout(init, 100);
        return;
      }
      gid.initialize({
        client_id: clientId,
        callback: (resp: GsiCredentialResponse) => {
          setGoogleIdToken(resp.credential);
          onSignedIn();
        },
      });
      if (buttonRef.current) {
        gid.renderButton(buttonRef.current, {
          theme: "filled_black",
          size: "large",
          type: "standard",
          text: "signin_with",
        });
      }
      gid.prompt(); // also try the One Tap auto-prompt
    };
    init();

    return () => { cancelled = true; };
  }, [clientId, onSignedIn]);

  // Local dev with no GOOGLE_CLIENT_ID — admin is open, this prompt
  // shouldn't render at all (api.me would have returned is_admin=true).
  // If we're here without a clientId, surface a clear error.
  if (!clientId) {
    return (
      <div className="mx-auto max-w-lg">
        <Card>
          <CardContent className="space-y-3 p-8">
            <h1 className="font-display text-xl font-semibold">Admin not configured</h1>
            <p className="text-sm text-muted-foreground">
              The <code>GOOGLE_CLIENT_ID</code> env var isn't set on this deployment, so
              admin sign-in is unavailable. Ask the platform team to provision an OAuth
              2.0 Web client and set the env var on the Cloud Run service.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/">Back to survey</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardContent className="space-y-4 p-8">
          <h1 className="font-display text-xl font-semibold">Admin sign-in</h1>
          {haveToken && deniedEmail ? (
            <p className="text-sm text-destructive">
              <span className="font-mono">{deniedEmail}</span> is not on the admin
              allowlist. Sign in with a Property Meld Google account.
            </p>
          ) : haveToken ? (
            <p className="text-sm text-muted-foreground">
              Your sign-in session expired. Sign in again to continue.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Property Meld admins only. Sign in with your propertymeld.com Google account.
            </p>
          )}
          <div ref={buttonRef} className="flex justify-center" />
          <Button asChild variant="ghost" size="sm">
            <Link to="/">Back to survey</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Row --------------------------------------------------------------

const BUSINESS_MODEL_LABEL: Record<string, string> = {
  owned: "Owned",
  mixed: "Mixed",
  managed: "Managed",
};

const CONTRACT_LABEL: Record<string, string> = {
  "markup-internal-only": "Markup internal",
  "markup-internal-and-invoices": "Markup internal + invoices",
  "pass-through": "Pass-through",
  "flat-rate-per-owner": "Flat rate for maintenance",
};

function Row({ s }: { s: SubmissionResponse }) {
  const submitter =
    s.punchout.user_name || s.punchout.user_email || s.iap_email || "—";
  const showContract = s.config.businessModel !== "owned";
  return (
    <tr className="hover:bg-secondary/50">
      <td className="px-4 py-3 align-top">
        <div className="text-foreground">
          {new Date(s.created_at).toLocaleDateString()}
        </div>
        <div className="text-xs text-muted-foreground">
          {new Date(s.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </td>
      <td className="px-4 py-3 align-top font-medium">
        {s.punchout.company || <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-4 py-3 align-top">
        <div>{submitter}</div>
        <div className="text-xs text-muted-foreground">{s.punchout.user_email || s.iap_email}</div>
      </td>
      <td className="px-4 py-3 align-top">
        {s.punchout.mid ? (
          <code className="text-xs">{s.punchout.mid}</code>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        {s.config.businessModel
          ? <Badge variant="muted">{BUSINESS_MODEL_LABEL[s.config.businessModel] ?? s.config.businessModel}</Badge>
          : <span className="text-xs text-muted-foreground">—</span>}
      </td>
      <td className="px-4 py-3 align-top">
        <div>{s.config.pas ?? <span className="text-xs text-muted-foreground">—</span>}</div>
        <div className="text-xs text-muted-foreground">{s.config.corporate ?? "—"}</div>
      </td>
      <td className="px-4 py-3 align-top">
        {showContract && s.config.contractModel ? (
          <span className="text-xs">{CONTRACT_LABEL[s.config.contractModel] ?? s.config.contractModel}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right align-top tabular-nums">
        ${s.config.shopRate.toFixed(0)}
      </td>
      <td className="px-4 py-3 align-top">
        {s.punchout.openToFollowup ? (
          <Badge variant="success">Yes</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">No</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex justify-end gap-1">
          <Button asChild variant="ghost" size="sm">
            <a href={`/api/submissions/${s.id}/documents/config`} target="_blank" rel="noreferrer">
              <ExternalLink /> Config
            </a>
          </Button>
        </div>
      </td>
    </tr>
  );
}
